import { Box, Card, CardContent, Chip, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { useState } from "react";

import {
  BulkActionBar,
  BulkButton,
  BulkCheckbox,
  useBulkSelection,
} from "../../components/bulk-selection/index.js";
import { PageHeader } from "../../components/PageHeader.js";
import { BulkDeleteDialog } from "../../components/ui/bulk-delete-dialog.js";
import { ConfirmDialog } from "../../components/ui/confirm-dialog.js";
import { BackButton } from "@/components/ui/back-button";
import { PageLoader } from "@/components/ui/page-loader";
import {
  bulkDeleteCategories,
  deleteCategory,
  listCategories,
  type CategoryDocumentsMode,
} from "./api.js";
import { CategoryDocsModePicker } from "./CategoryDocsModePicker.js";
import { EditCategoryDialog } from "./EditCategoryDialog.js";

/**
 * Category management for the Documentation tab.
 *
 * Lists every KB category the staff dashboard knows about — visible and
 * hidden — and lets editors prune them in two flavours:
 *   • Single row → trash icon → <ConfirmDialog>
 *   • Multi-row → checkboxes + <BulkActionBar> → <BulkDeleteDialog>
 *
 * Deleting a category **cascades** the full document destroy pipeline
 * onto every doc it owned: DigitalOcean Spaces attachments are purged
 * (`xop-storage` bucket), AI Gateway vector entries dropped, and the
 * doc row discarded — same `Documents::PurgeAndRemove` service the
 * docs controller uses for direct doc deletes. The doc-count nudge in
 * each dialog tells the operator how many docs will go with the
 * category.
 *
 * Selection uses the shared `useBulkSelection` module — same Gmail-style
 * shift-click + indeterminate behaviour the support ticket list and SLA
 * settings panel use. After a successful delete we invalidate every
 * `docs.*` query so the category filter dropdown on the Documents page
 * refreshes immediately.
 */
export function CategoriesManagePage(): JSX.Element {
  const qc = useQueryClient();

  // Editors hitting `/api/documentation/categories` land on the Rails
  // admin namespace (the dashboard proxy switches on the
  // `documentation:edit` permission), so hidden categories show up
  // alongside visible ones and `document_count` is populated.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["docs", "categories"],
    queryFn: listCategories,
  });

  const rows = data?.data ?? [];
  const bulk = useBulkSelection(rows);

  const [pendingDelete, setPendingDelete] = useState<{
    id: number;
    name: string;
    document_count: number;
  } | null>(null);
  const [pendingEdit, setPendingEdit] = useState<{
    id: number;
    name: string;
    visible: boolean;
    visibleToResellers: boolean;
  } | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  // What should happen to the docs under the categories being removed?
  // Defaults to "destroy" (cascade-purge) to match the trash icon's
  // intent; the operator can flip to "keep" (move to Uncategorized)
  // inside each dialog. Each dialog has its own mode so a single-row
  // delete and a multi-row bulk delete can disagree without bleeding.
  const [singleMode, setSingleMode] =
    useState<CategoryDocumentsMode>("destroy");
  const [bulkMode, setBulkMode] = useState<CategoryDocumentsMode>("destroy");

  const invalidate = (): Promise<void> =>
    qc.invalidateQueries({ queryKey: ["docs"] }).then(() => undefined);

  const remove = useMutation({
    mutationFn: (args: { id: number; mode: CategoryDocumentsMode }) =>
      deleteCategory(args.id, args.mode),
    onSuccess: () => void invalidate(),
  });

  const bulkRemove = useMutation({
    mutationFn: (args: { ids: number[]; mode: CategoryDocumentsMode }) =>
      bulkDeleteCategories(args.ids, args.mode),
    onSuccess: () => {
      bulk.clear();
      setConfirmBulk(false);
      void invalidate();
    },
    // The shared <BulkDeleteDialog> swallows errors from its `onConfirm`
    // callback to keep itself open, so without this `onError` the user
    // sees the dialog hanging with no feedback when the proxy / Rails
    // call fails. Logging + the inline banner below give the operator
    // a real error path instead of a silent dead-end.
    onError: (err) => {
      console.error("[CategoriesManagePage] bulk delete failed", err);
    },
  });

  const selectedRows = rows.filter((r) => bulk.isSelected(r.id));

  return (
    <Box sx={{ display: "grid", gap: 3 }}>
      <PageHeader
        title="Manage categories"
        subtitle="Group your documentation. Deleting a category also deletes every document inside it — files, vector entries, and all."
        right={
          <BackButton to="/documentation" label="Back to documents" />
        }
      />

      <BulkActionBar count={bulk.count} onClear={bulk.clear}>
        <BulkButton
          variant="destructive"
          disabled={bulkRemove.isPending}
          onClick={() => setConfirmBulk(true)}
        >
          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
          Delete {bulk.count} {bulk.count === 1 ? "category" : "categories"}
        </BulkButton>
      </BulkActionBar>

      {/* Inline error banner — surfaces the real upstream failure
       * (DOCS_UPSTREAM_ERROR, network, permission) instead of leaving
       * the user with a silently-stuck dialog. The dialog itself
       * keeps swallowing the throw so the dialog body doesn't need
       * to render the error twice. */}
      {(remove.isError || bulkRemove.isError) && (
        <Box
          role="alert"
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "error.main",
            bgcolor: (t) => alpha(t.palette.error.main, 0.08),
            px: 2,
            py: 1.5,
            fontSize: 14,
            color: "error.main",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {bulkRemove.isError ? "Bulk delete failed" : "Delete failed"}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
            {(bulkRemove.error ?? remove.error) instanceof Error
              ? (bulkRemove.error ?? remove.error)!.message
              : "Unknown error. Check the browser console + dashboard server logs."}
          </Typography>
        </Box>
      )}

      {isLoading && <PageLoader />}

      {isError && (
        <Card>
          <CardContent sx={{ display: "grid", gap: 1, py: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "error.main" }}>
              Couldn't load categories.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              {error instanceof Error ? error.message : "Unknown error"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Card>
          <CardContent sx={{ display: "grid", gap: 1, py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
              No categories yet.
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              Categories get created the first time you tag a document.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <Card sx={{ overflow: "hidden" }}>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, "& > li + li": { borderTop: 1, borderColor: "divider" } }}>
            <Box
              component="li"
              data-testid="categories-select-all-row"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
                px: 2.5,
                py: 1,
                fontSize: 12,
                color: "text.disabled",
              }}
            >
              <BulkCheckbox
                checked={bulk.allOnPageChecked}
                indeterminate={bulk.someOnPageChecked}
                onChange={(checked) => bulk.toggleAllOnPage(checked)}
                ariaLabel="Select all categories"
              />
              <Box component="span">{`Select all (${rows.length})`}</Box>
            </Box>
            {rows.map((c) => {
              const docCount = c.document_count ?? 0;
              return (
                <Box
                  component="li"
                  key={c.id}
                  data-testid={`categories-row-${c.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    transition: "background-color 150ms",
                    "&:hover": { bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.5) },
                    "&:hover .doc-cat-controls": { opacity: 1 },
                  }}
                >
                  <BulkCheckbox
                    checked={bulk.isSelected(c.id)}
                    onChange={(checked, opts) =>
                      bulk.setChecked(c.id, checked, opts)
                    }
                    ariaLabel={`Select category ${c.name}`}
                  />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ mb: 0.5, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                      <Typography
                        component="h3"
                        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 14, fontWeight: 500, lineHeight: 1.25, color: "text.primary" }}
                      >
                        {c.name}
                      </Typography>
                      {c.visible_in_customer_portal === false && (
                        <Chip
                          label="Hidden"
                          size="small"
                          sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "surfaceSoft", color: "text.secondary" }}
                        />
                      )}
                      {/* The reseller audience is opt-in and off by default,
                          so the informative chip is the POSITIVE one: a
                          "Hidden from resellers" marker would sit on almost
                          every row and say nothing. */}
                      {c.visible_in_reseller_portal === true && (
                        <Chip
                          label="Resellers"
                          size="small"
                          data-testid={`categories-row-resellers-${c.id}`}
                          sx={{ height: 20, borderRadius: 0.75, fontSize: 11, bgcolor: "action.selected", color: "primary.main" }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, fontSize: "11px", color: "text.disabled" }}>
                      <Box component="span">{c.slug}</Box>
                      <Box component="span">
                        {`· ${docCount.toLocaleString()} document${docCount === 1 ? "" : "s"}`}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    className="doc-cat-controls"
                    sx={{ display: "flex", flexShrink: 0, alignItems: "center", gap: 0.5, opacity: 0.8, transition: "opacity 150ms" }}
                  >
                    <IconButton
                      size="small"
                      title={`Rename category ${c.name}`}
                      aria-label={`Edit category ${c.name}`}
                      data-testid={`categories-row-edit-${c.id}`}
                      onClick={() =>
                        setPendingEdit({
                          id: c.id,
                          name: c.name,
                          visible: c.visible_in_customer_portal !== false,
                          visibleToResellers: c.visible_in_reseller_portal === true,
                        })
                      }
                      sx={{ color: "text.disabled" }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={`Delete category ${c.name}`}
                      aria-label={`Delete category ${c.name}`}
                      data-testid={`categories-row-delete-${c.id}`}
                      onClick={() =>
                        setPendingDelete({
                          id: c.id,
                          name: c.name,
                          document_count: docCount,
                        })
                      }
                      sx={{ color: "error.main" }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Card>
      )}

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setSingleMode("destroy");
        }}
        title="Delete category?"
        description={
          pendingDelete
            ? `"${pendingDelete.name}" will be removed. This cannot be undone.`
            : ""
        }
        confirmLabel={
          pendingDelete && pendingDelete.document_count > 0 && singleMode === "keep"
            ? "Delete category, keep documents"
            : "Delete category"
        }
        onConfirm={async () => {
          if (!pendingDelete) return;
          await remove.mutateAsync({ id: pendingDelete.id, mode: singleMode });
        }}
      >
        {pendingDelete && pendingDelete.document_count > 0 && (
          <CategoryDocsModePicker
            value={singleMode}
            onChange={setSingleMode}
            documentCount={pendingDelete.document_count}
            idPrefix={`single-${pendingDelete.id}`}
          />
        )}
      </ConfirmDialog>

      <BulkDeleteDialog
        isOpen={confirmBulk}
        onClose={() => {
          setConfirmBulk(false);
          setBulkMode("destroy");
        }}
        items={selectedRows.map((c) => ({
          id: c.id,
          name: c.name,
          document_count: c.document_count ?? 0,
        }))}
        entityLabel={{ singular: "Category", plural: "Categories" }}
        cascadingWarning={
          bulkMode === "destroy"
            ? "Every document inside these categories will also be deleted — files purged from object storage, vector entries removed."
            : "Documents inside these categories stay in place — they move to the Uncategorized group with their files and vector entries intact."
        }
        renderItem={(it) => <>{it.name}</>}
        renderItemBadge={(it) =>
          it.document_count > 0 ? (
            <Box component="span" sx={{ fontSize: "10px", color: "error.dark" }}>
              {it.document_count} doc{it.document_count === 1 ? "" : "s"}
              {bulkMode === "destroy" ? " → deleted" : " → uncategorized"}
            </Box>
          ) : null
        }
        onConfirm={async () => {
          await bulkRemove.mutateAsync({
            ids: [...bulk.selected],
            mode: bulkMode,
          });
        }}
      >
        <CategoryDocsModePicker
          value={bulkMode}
          onChange={setBulkMode}
          documentCount={selectedRows.reduce(
            (sum, c) => sum + (c.document_count ?? 0),
            0,
          )}
          idPrefix="bulk"
        />
      </BulkDeleteDialog>

      <EditCategoryDialog
        target={pendingEdit}
        onClose={() => setPendingEdit(null)}
        onSaved={() => {
          setPendingEdit(null);
          void invalidate();
        }}
      />
    </Box>
  );
}
